import { z } from 'zod';

import { RESOURCE_KINDS } from './allocations.js';
import { ResourceRoleResponseSchema } from './org.js';
import { ResourceUtilisationResponseSchema } from './perspectives.js';
import {
  periodBucket,
  UtilisationGranularitySchema,
  UtilisationModeSchema,
  type UtilisationGranularity,
  type UtilisationMode,
} from './utilisation.js';

/**
 * Planner read model (M10/M11 UI — the Resource Planner page). One call for
 * the whole tenant: every resource's per-period utilisation PLUS an
 * allocation directory (named drill rows, generic demand slots, registered
 * hours) so the People and Competency-demand views render from one response.
 * Kept apart from `ResourcePerspectiveQuerySchema` — that contract's
 * exactly-one-scope refine is shipped, and this view needs all three scopes
 * at once.
 */

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO date (YYYY-MM-DD)');

export const PlannerPerspectiveQuerySchema = z.object({
  from: isoDate,
  to: isoDate,
  granularity: UtilisationGranularitySchema.default('week'),
  mode: UtilisationModeSchema.default('raw'),
});
export type PlannerPerspectiveQuery = z.infer<typeof PlannerPerspectiveQuerySchema>;

export const PlannerPeriodHoursSchema = z.object({
  period: z.string(),
  hours: z.number(),
});
export type PlannerPeriodHours = z.infer<typeof PlannerPeriodHoursSchema>;

export const PlannerAllocationSchema = z.object({
  allocationId: z.string().uuid(),
  resourceKind: z.enum(RESOURCE_KINDS),
  /** Set for NAMED allocations. */
  resourceId: z.string().uuid().nullable(),
  /** Set for GENERIC allocations (and optionally as a NAMED competency stamp, DL-007). */
  roleId: z.string().uuid().nullable(),
  workItemId: z.string().uuid(),
  workItemName: z.string(),
  rootProjectId: z.string().uuid(),
  projectName: z.string(),
  projectNumber: z.string().nullable(),
  /** The root project's probability; 100 when unset (DL-032). */
  probability: z.number().int(),
  status: z.string(),
  allocatedBudget: z.number(),
  /** From registered_hours_summaries; 0 when the allocation has none. */
  registeredHours: z.number(),
  /** Bucketed active booking hours; empty when the allocation has no bookings in range. */
  periods: z.array(PlannerPeriodHoursSchema),
});
export type PlannerAllocation = z.infer<typeof PlannerAllocationSchema>;

export const PlannerPerspectiveResponseSchema = z.object({
  mode: UtilisationModeSchema,
  granularity: UtilisationGranularitySchema,
  /** Every tenant resource, zero-filled across the range's buckets. */
  resources: z.array(ResourceUtilisationResponseSchema),
  /** Every effectively-active, non-retired allocation (named and generic). */
  allocations: z.array(PlannerAllocationSchema),
});
export type PlannerPerspectiveResponse = z.infer<typeof PlannerPerspectiveResponseSchema>;

// ---------------------------------------------------------------------------
// Availability (Find & Allocate — capacity minus booked over a free date range)
// ---------------------------------------------------------------------------

export const AvailabilityQuerySchema = z.object({
  from: isoDate,
  to: isoDate,
});
export type AvailabilityQuery = z.infer<typeof AvailabilityQuerySchema>;

export const AvailabilityResourceSchema = z.object({
  resourceId: z.string().uuid(),
  capacityHours: z.number(),
  /** §1.4-gated NAMED booking hours in the range (raw — availability is never weighted). */
  bookedHours: z.number(),
});
export type AvailabilityResource = z.infer<typeof AvailabilityResourceSchema>;

export const AvailabilityResponseSchema = z.object({
  resources: z.array(AvailabilityResourceSchema),
});
export type AvailabilityResponse = z.infer<typeof AvailabilityResponseSchema>;

// ---------------------------------------------------------------------------
// Resource search (Typesense-enriched in live lookup mode)
// ---------------------------------------------------------------------------

export const ResourceSearchQuerySchema = z.object({
  q: z.string().max(200).default(''),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ResourceSearchQuery = z.infer<typeof ResourceSearchQuerySchema>;

export const ResourceSearchItemSchema = z.object({
  resourceId: z.string().uuid(),
  name: z.string(),
  departmentId: z.string().uuid(),
  roles: z.array(ResourceRoleResponseSchema),
  /** Enrichment from the TimeLog employee index (live lookup mode only). */
  email: z.string().nullable(),
  position: z.string().nullable(),
  initials: z.string().nullable(),
});
export type ResourceSearchItem = z.infer<typeof ResourceSearchItemSchema>;

export const ResourceSearchResponseSchema = z.object({
  items: z.array(ResourceSearchItemSchema),
});
export type ResourceSearchResponse = z.infer<typeof ResourceSearchResponseSchema>;

// ---------------------------------------------------------------------------
// Pure bucketing for the allocation directory
// ---------------------------------------------------------------------------

export type AllocationBookingRow = {
  allocationId: string;
  date: string;
  hours: number;
  /** The owning root project's probability; 100 when unset. */
  probability: number;
};

export type BucketAllocationHoursOptions = {
  mode: UtilisationMode;
  granularity: UtilisationGranularity;
};

const toHundredths = (hours: number): number => Math.round(hours * 100);

/**
 * Per-allocation per-period booking sums, DL-032-weighted in `weighted` mode.
 * Same integer-hundredths discipline as `aggregateUtilisation` (weighting
 * multiplies hundredths by the integer probability, dividing by 100 only at
 * the edge) so allocation cells and resource cells never disagree on
 * rounding. Periods with no bookings are omitted; keys sort lexicographically
 * within one granularity (ISO Mondays, YYYY-MM, YYYY-Qn).
 */
export const bucketAllocationHours = (
  rows: AllocationBookingRow[],
  options: BucketAllocationHoursOptions,
): Map<string, PlannerPeriodHours[]> => {
  // allocationId → period → hour-ten-thousandths.
  const sums = new Map<string, Map<string, number>>();
  for (const row of rows) {
    const period = periodBucket(row.date, options.granularity);
    const weight = options.mode === 'weighted' ? row.probability : 100;
    const byPeriod = sums.get(row.allocationId) ?? new Map<string, number>();
    byPeriod.set(period, (byPeriod.get(period) ?? 0) + toHundredths(row.hours) * weight);
    sums.set(row.allocationId, byPeriod);
  }
  const result = new Map<string, PlannerPeriodHours[]>();
  for (const [allocationId, byPeriod] of sums) {
    const periods = [...byPeriod.entries()]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([period, tenThousandths]) => ({
        period,
        hours: Math.round(tenThousandths / 100) / 100,
      }));
    result.set(allocationId, periods);
  }
  return result;
};
